import {ApiError, ErrorAlert, ProblemJson} from "@variocube/app-ui";
import React, {useEffect, useRef} from "react";
import {useLocalization} from "../i18n";

/**
 * Renders a prolongation failure.
 *
 * A known *logical* error — identified by the RFC-7807 {@code code} the backend attaches (e.g. {@code NO_BOX_AVAILABLE})
 * — gets a clear, localized message and nothing else. Anything else is treated as *technical*: a friendly localized
 * headline plus the raw technical {@code detail}/{@code status} underneath (via {@link ErrorAlert}), so an unexpected
 * failure stays debuggable instead of collapsing to an opaque "Bad Request".
 */
export function ProlongError({error}: { error: unknown }) {
	const {t} = useLocalization();
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		ref.current?.scrollIntoView({behavior: "smooth", block: "center"});
	}, [error]);

	let problem: Partial<ProblemJson>;
	if (error instanceof ApiError) {
		// Logical errors (identified by a stable backend code) get a self-explanatory message and no technical noise;
		// everything else keeps a friendly headline but shows the technical detail/status so it stays debuggable.
		const logicalMessage = logicalMessageFor(error.code, t);
		problem = logicalMessage
			? {title: logicalMessage}
			: {title: t("prolong.error.generic"), detail: error.detail ?? error.title, status: error.status};
	}
	else if (error instanceof Error) {
		problem = {title: t("prolong.error.generic"), detail: error.message};
	}
	else {
		problem = {title: t("prolong.error.generic")};
	}

	return <ErrorAlert ref={ref} error={problem} />;
}

function logicalMessageFor(code: unknown, t: (key: "prolong.error.noBox" | "prolong.error.amountTooLow") => string) {
	switch (code) {
		case "NO_BOX_AVAILABLE":
			return t("prolong.error.noBox");
		case "PAYMENT_AMOUNT_TOO_LOW":
			return t("prolong.error.amountTooLow");
		default:
			return undefined;
	}
}
